import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Calculator, Clock, Zap, Shield, CheckCircle } from 'lucide-react';
import FilloutButton from '@/components/FilloutButton';

const JotformPricingCalculator = () => {
  const [formCount, setFormCount] = useState([5]);
  const [complexity, setComplexity] = useState(1); // 0: Simple, 1: Medium, 2: Complex
  const [hasIntegrations, setHasIntegrations] = useState(false);
  const [hasPayments, setHasPayments] = useState(false);
  const [isRush, setIsRush] = useState(false);
  const [totalPrice, setTotalPrice] = useState(0);

  const complexityOptions = [
    { label: 'Simple Forms', multiplier: 1, description: 'Basic forms with standard fields' },
    { label: 'Medium Complexity', multiplier: 1.5, description: 'Conditional logic and calculations' },
    { label: 'Advanced Forms', multiplier: 2.2, description: 'Complex workflows and automations' }
  ];

  useEffect(() => {
    const basePrice = 150; // Base price per form
    const forms = formCount[0];
    const complexityMultiplier = complexityOptions[complexity].multiplier;
    
    let price = forms * basePrice * complexityMultiplier;
    
    if (hasIntegrations) price += forms * 75; // Integration cost
    if (hasPayments) price += forms * 100; // Payment integration
    if (isRush) price *= 1.5; // Rush delivery
    
    // Volume discount
    if (forms >= 10) price *= 0.9; // 10% discount for 10+ forms
    if (forms >= 20) price *= 0.85; // 15% total discount for 20+ forms
    
    setTotalPrice(Math.round(price));
  }, [formCount, complexity, hasIntegrations, hasPayments, isRush]);

  const deliveryTime = () => {
    const baseDays = formCount[0] * (complexity + 1) * 2;
    return isRush ? Math.ceil(baseDays / 2) : baseDays;
  };

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-primary/5 via-background to-accent/5">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center space-y-3 sm:space-y-4 mb-12 sm:mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold">
            Project Calculator
          </h2>
          <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto">
            Get an instant estimate for your Jotform project
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-12 max-w-6xl mx-auto">
          {/* Calculator Form */}
          <Card className="relative overflow-hidden border-primary/20 shadow-xl order-2 lg:order-1">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-accent/5"></div>
            <CardHeader className="relative z-10 pb-4 sm:pb-6">
              <CardTitle className="flex items-center gap-3 text-lg sm:text-xl">
                <Calculator className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                Configure Your Project
              </CardTitle>
            </CardHeader>
            <CardContent className="relative z-10 space-y-6 sm:space-y-8 p-4 sm:p-6">
              {/* Form Count */}
              <div className="space-y-3 sm:space-y-4">
                <label className="text-sm font-medium">Number of Forms: {formCount[0]}</label>
                <Slider
                  value={formCount}
                  onValueChange={setFormCount}
                  max={50}
                  min={1}
                  step={1}
                  className="w-full touch-manipulation"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>1 form</span>
                  <span>50+ forms</span>
                </div>
              </div>

              {/* Complexity */}
              <div className="space-y-3 sm:space-y-4">
                <label className="text-sm font-medium">Project Complexity</label>
                <div className="grid gap-2 sm:gap-3">
                  {complexityOptions.map((option, index) => (
                    <Card 
                      key={index}
                      className={`cursor-pointer border-2 transition-all duration-300 ${
                        complexity === index 
                          ? 'border-primary bg-primary/5' 
                          : 'border-muted hover:border-primary/50'
                      }`}
                      onClick={() => setComplexity(index)}
                    >
                      <CardContent className="p-3 sm:p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-sm sm:text-base truncate">{option.label}</h4>
                            <p className="text-xs sm:text-sm text-muted-foreground leading-tight">{option.description}</p>
                          </div>
                          <Badge variant={complexity === index ? "default" : "secondary"} className="text-xs flex-shrink-0 ml-2">
                            {option.multiplier}x
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Add-ons */}
              <div className="space-y-3 sm:space-y-4">
                <label className="text-sm font-medium">Additional Services</label>
                <div className="space-y-3 sm:space-y-4">
                  <div className="flex items-center justify-between p-3 sm:p-4 border rounded-lg">
                    <div className="flex-1 min-w-0 mr-3">
                      <h4 className="font-medium text-sm sm:text-base">CRM/API Integrations</h4>
                      <p className="text-xs sm:text-sm text-muted-foreground">Connect to external systems</p>
                    </div>
                    <Switch checked={hasIntegrations} onCheckedChange={setHasIntegrations} className="flex-shrink-0" />
                  </div>
                  
                  <div className="flex items-center justify-between p-3 sm:p-4 border rounded-lg">
                    <div className="flex-1 min-w-0 mr-3">
                      <h4 className="font-medium text-sm sm:text-base">Payment Processing</h4>
                      <p className="text-xs sm:text-sm text-muted-foreground">Stripe, PayPal integration</p>
                    </div>
                    <Switch checked={hasPayments} onCheckedChange={setHasPayments} className="flex-shrink-0" />
                  </div>
                  
                  <div className="flex items-center justify-between p-3 sm:p-4 border rounded-lg">
                    <div className="flex-1 min-w-0 mr-3">
                      <h4 className="font-medium text-sm sm:text-base">Rush Delivery</h4>
                      <p className="text-xs sm:text-sm text-muted-foreground">50% faster completion</p>
                    </div>
                    <Switch checked={isRush} onCheckedChange={setIsRush} className="flex-shrink-0" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Price Display */}
          <div className="space-y-4 sm:space-y-6 order-1 lg:order-2">
            {/* Main Price Card */}
            <Card className="relative overflow-hidden border-primary/20 shadow-xl bg-gradient-to-br from-background to-primary/5">
              <CardHeader>
                <CardTitle className="text-center text-2xl">
                  Project Estimate
                </CardTitle>
              </CardHeader>
              <CardContent className="text-center space-y-6">
                <div className="relative">
                  <div className="text-5xl font-bold text-primary animate-pulse">
                    ${totalPrice.toLocaleString()}
                  </div>
                  <div className="text-muted-foreground">Total Project Cost</div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-2 justify-center">
                    <Clock className="w-5 h-5 text-blue-500" />
                    <div>
                      <div className="font-semibold">{deliveryTime()} days</div>
                      <div className="text-xs text-muted-foreground">Delivery time</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 justify-center">
                    <Zap className="w-5 h-5 text-yellow-500" />
                    <div>
                      <div className="font-semibold">{formCount[0]} forms</div>
                      <div className="text-xs text-muted-foreground">Total forms</div>
                    </div>
                  </div>
                </div>

                {formCount[0] >= 10 && (
                  <Badge className="bg-green-500/10 text-green-700 border-green-200">
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Volume Discount Applied!
                  </Badge>
                )}

                <FilloutButton className="w-full bg-primary hover:bg-primary/90 text-primary-foreground py-6 text-lg font-semibold">
                  Get Detailed Quote
                </FilloutButton>
              </CardContent>
            </Card>

            {/* Features Included */}
            <Card className="border-primary/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Shield className="w-5 h-5 text-primary" />
                  What's Included
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {[
                    'Unlimited form submissions',
                    'Mobile-responsive design',
                    'HIPAA compliance (if needed)',
                    '30 days of free support',
                    'Form analytics setup',
                    'Documentation & training'
                  ].map((feature, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Trust Signals */}
            <div className="text-center space-y-3">
              <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  No Hidden Fees
                </div>
                <div className="flex items-center gap-1">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Fixed Price Guarantee
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                * Final price may vary based on specific requirements
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default JotformPricingCalculator;